/**
 * CihCoachCell — single coach tile used inside a CihCoachGrid. Soft rounded
 * card on the brand shadow ramp; sits inside a contained card grid.
 *
 * This is a deliberately separate component from the site-wide CoachCard,
 * which keeps its pill/shadow styling for use elsewhere.
 *
 * Aliases: coach cell, coach tile
 */

import { Link } from "@tanstack/react-router";

import { CDNImage } from "~/components/ui/cdn-image";
import { NamedIcon } from "~/components/ui/named-icon";

export type CihCoachCellData = {
  readonly id: string;
  readonly username: string;
  readonly fullName: string;
  readonly profilePictureUrl: string | null;
  readonly credential: string | null;
  readonly description: string | null;
};

/** Keeps only the tile fields, so a loader sends the browser nothing else. */
export function toCihCoachCell(coach: CihCoachCellData): CihCoachCellData {
  return {
    id: coach.id,
    username: coach.username,
    fullName: coach.fullName,
    profilePictureUrl: coach.profilePictureUrl,
    credential: coach.credential,
    description: coach.description,
  };
}

/**
 * Three-column grid of coach tiles, shared by the coach lists of challenges,
 * frameworks, organizations and training programs.
 */
export function CihCoachCellGrid({
  coaches,
}: {
  readonly coaches: ReadonlyArray<CihCoachCellData>;
}) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5 lg:grid-cols-3">
      {coaches.map((coach) => (
        <CihCoachCell key={coach.id} coach={coach} />
      ))}
    </div>
  );
}

export function CihCoachCell({ coach }: { readonly coach: CihCoachCellData }) {
  return (
    <Link
      to="/$username"
      params={{ username: coach.username }}
      className="flex cursor-pointer items-start gap-4 rounded-2xl border border-gray-100 bg-white p-6 shadow-xs transition duration-300 hover:-translate-y-0.5 hover:border-gray-200 hover:shadow-md"
    >
      {coach.profilePictureUrl ? (
        <CDNImage
          src={coach.profilePictureUrl}
          alt=""
          className="h-11 w-11 shrink-0 rounded-full object-cover"
          width={88}
          height={88}
          loading="lazy"
        />
      ) : (
        <div
          aria-hidden="true"
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gray-100"
        >
          <NamedIcon name="users" className="h-5 w-5 text-gray-400" />
        </div>
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-gray-900">
          {coach.fullName}
        </div>
        {coach.credential ? (
          <div className="text-xs text-gray-500">{coach.credential}</div>
        ) : null}
        {coach.description ? (
          <p className="mt-2 line-clamp-2 text-xs leading-relaxed text-gray-600">
            {coach.description}
          </p>
        ) : null}
      </div>
    </Link>
  );
}
